import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import classnames from "classnames/bind";

import AppErrorHandler from "./components/AppErrorHandler";
import LoginPopup from "./components/LoginPopup";
import Submenu from "./components/Submenu";
import Search from "./components/Search";
import MainMenu from "./components/MainMenu";
import Popups from "./components/Popups";
import LoginButtons from "./components/LoginButtons";
import Logo from "./components/Logo";
import Burger from "./components/Burger/Burger";
import MainButtons from "./components/MainButtons";
import BottomMenu from "./components/BottomMenu";
import Profile from "./components/Profile";
import RegistrationPopup from "./components/RegistrationPopup";
import LanguageSelect from "./components/LanguageSelect";
import ErrorPlank from "./components/ErrorPlank";

import Features from "./devFeatures";

import callWindowFunction from "./utils/callWindowFunction";

import { headerHeight, MAIN_PAGE_QUERY_PARAM, THEMES } from "./consts";

import logger from "./logger";
import { useStore } from "./connect";

import styles from "./Header.scss";

const OutdatedBrowserPopup = React.lazy(() =>
  import(
    /* webpackChunkName: "OutdatedBrowserPopup" */ "./components/OutdatedBrowserPopup"
  )
);
const LanguageInformer = React.lazy(() =>
  import(
    /* webpackChunkName: "LanguageInformer" */ "./components/LanguageInformer"
  )
);
const Nps = React.lazy(() =>
  import(/* webpackChunkName: "Nps" */ "./components/Nps")
);
const SecuritySurvey = React.lazy(() =>
  import(/* webpackChunkName: "SecuritySurvey" */ "./components/SecuritySurvey")
);
const Feed = React.lazy(() =>
  import(/* webpackChunkName: "Feed" */ "./components/Feed")
);
const CookieBanner = React.lazy(() =>
  import(/* webpackChunkName: "CookieBanner" */ "./components/CookieBanner")
);

const cx = classnames.bind(styles);

const Header = ({ onUserLogin, onRestorePasswordRedirect }) => {
  const {
    baseUrl,
    theme,
    search = {},
    nps = {},
    securitySurvey = {},
    profile = {},
    header = {},
    outdatedBrowser = {},
    adaptive = {},
    languageInformer = {},
  } = useStore();

  const searchKeydownFunction = (event) => {
    if (search.searchVisible && event.keyCode === 27) {
      search.hideSearch();
    }

    if (event.altKey && event.keyCode === 70) {
      search.showSearch(event);
    }
  };

  const handleServiceUnavailable = () => {
    profile.setNoProfile(true);
    profile.setUser({});
  };

  useEffect(() => {
    document.addEventListener("keydown", searchKeydownFunction);

    callWindowFunction("publishToEventBus", [
      {
        subject: "headerHasBeenMounted",
        destination: "thisTab",
        body: {
          result: "header has been mounted",
        },
      },
    ]);

    return () => {
      document.removeEventListener("keydown", searchKeydownFunction);
    };
  }, []);

  const mediaMatchFastReg = () => {
    let res = false;

    try {
      res = window.matchMedia("(min-width: 900px)").matches;
    } catch (error) {
      console.error("Error with mediaMatchFastReg: ", error);
      logger.error(error, {
        sentryTitle: "Error with mediaMatchFastReg",
      });
    }

    return res;
  };

  return (
    <>
      {!process.env.PRERENDER && (
        <Features handleServiceUnavailable={handleServiceUnavailable} />
      )}
      <div
        data-app="header"
        data-theme={theme === THEMES.OLD_THEME ? THEMES.DEFAULT_THEME : theme}
        className={cx("header-container")}
      >
        {languageInformer.visible && (
          <AppErrorHandler slot="LanguageInformer">
            <React.Suspense fallback="">
              <LanguageInformer />
            </React.Suspense>
          </AppErrorHandler>
        )}
        <AppErrorHandler slot="Update popups">
          {!process.env.PRERENDER && <Popups />}
        </AppErrorHandler>
        {profile.isLoggedIn && nps.show && (
          <React.Suspense fallback="">
            <AppErrorHandler slot="nps">
              <Nps />
            </AppErrorHandler>
          </React.Suspense>
        )}
        {profile.isLoggedIn && securitySurvey.show && (
          <React.Suspense fallback="">
            <AppErrorHandler slot="SecuritySurvey">
              <SecuritySurvey />
            </AppErrorHandler>
          </React.Suspense>
        )}
        {search.showExtendedSearchPopup && (
          <iframe
            id="extended-firm-search"
            src={`https://${baseUrl}/extended-search/`}
            title="Extended firm search"
            frameBorder="0"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 999999,
              width: "100%",
              height: "100%",
            }}
          />
        )}
        {search.searchVisible &&
          window &&
          headerHeight - window.pageYOffset < 0 &&
          !header.sticky && <div style={{ height: headerHeight }} />}
        <header
          className={cx("header", {
            "header-search-sticky-active":
              search.searchVisible &&
              window &&
              headerHeight - window.pageYOffset < 0 &&
              !header.sticky,
            "is-anonim": !profile.isLoggedIn,
          })}
        >
          {!(
            (adaptive.isMobile || adaptive.isLargeMobile) &&
            search.searchVisible
          ) && (
            <AppErrorHandler slot="Burger and logo">
              <Burger />
              <Logo baseUrl={baseUrl} />
            </AppErrorHandler>
          )}

          <AppErrorHandler slot="Main menu">
            <MainMenu />
          </AppErrorHandler>

          {search.searchVisible && <Search />}

          {!(
            (adaptive.isMobile || adaptive.isLargeMobile) &&
            search.searchVisible
          ) &&
            profile.profileHasBeenReceived && (
              <div className={cx("after-nav-section")} id="afterNavSection">
                {profile.isLoggedIn || profile.noProfile ? (
                  <>
                    {!profile.noProfile && (
                      <AppErrorHandler slot="Main buttons">
                        <MainButtons />
                      </AppErrorHandler>
                    )}
                    <AppErrorHandler slot="Profile">
                      <Profile />
                    </AppErrorHandler>
                  </>
                ) : (
                  <AppErrorHandler slot="Language select and Login buttons">
                    <LanguageSelect />
                    <LoginButtons />
                  </AppErrorHandler>
                )}
              </div>
            )}
        </header>
        {profile.noProfile ? (
          <ErrorPlank />
        ) : (
          <AppErrorHandler slot="Submenu">
            <Submenu />
          </AppErrorHandler>
        )}

        <AppErrorHandler slot="Bottom menu">
          <BottomMenu />
        </AppErrorHandler>

        <div className={cx("divider")} />

        {profile.loginPopup && (
          <AppErrorHandler
            slot="loginPopup"
            handleErrorMessage={profile.handleLoginPopupError}
          >
            <LoginPopup
              baseUrl={baseUrl}
              onLoginClose={profile.onLoginClose}
              onUserLogin={onUserLogin}
              onRestorePasswordRedirect={onRestorePasswordRedirect}
              onUserRegister={
                profile.registrationPopupCookie
                  ? () =>
                      profile.onRegistrationOpen({
                        next: window.location.href,
                        additionalQueryParams: header.isMainPage
                          ? MAIN_PAGE_QUERY_PARAM
                          : "",
                      })
                  : profile.onUserRegister
              }
              fastreg={mediaMatchFastReg()}
              next={profile.loginPopupNext}
              ignoreIframe
              additionalQueryParams={profile.loginPopupAdditionalQueryParams}
            />
          </AppErrorHandler>
        )}
        {profile.registrationPopup && (
          <AppErrorHandler
            slot="registrationPopup"
            handleErrorMessage={profile.handleRegistrationPopupError}
          >
            <RegistrationPopup
              baseUrl={baseUrl}
              onRegistrationClose={profile.onRegistrationClose}
              onRegistrationSuccess={profile.onRegistrationSuccess}
              next={profile.registrationPopupNext}
              additionalQueryParams={profile.registerPopupAdditionalQueryParams}
            />
          </AppErrorHandler>
        )}
        {outdatedBrowser.showOutdatedBrowserPopup && (
          <React.Suspense fallback="">
            <AppErrorHandler slot="OutdatedBrowserPopup">
              <OutdatedBrowserPopup
                browser={outdatedBrowser.currentBrowser}
                onBrowserButtonClick={
                  outdatedBrowser.handleBrowserInformerClose
                }
                onRemindLaterClick={outdatedBrowser.handleBrowserInformerClose}
                onClose={outdatedBrowser.handleBrowserInformerClose}
                closeOnOverlayClick={false}
                baseUrl={baseUrl}
              />
            </AppErrorHandler>
          </React.Suspense>
        )}
        {profile.isLoggedIn && (
          <React.Suspense fallback="">
            <AppErrorHandler slot="feed">
              <Feed />
            </AppErrorHandler>
          </React.Suspense>
        )}
        {header.isCookieBannerVisible && (
          <React.Suspense fallback="">
            <AppErrorHandler slot="cookie-banner">
              <CookieBanner />
            </AppErrorHandler>
          </React.Suspense>
        )}
      </div>
    </>
  );
};

Header.propTypes = {
  onUserLogin: PropTypes.func,
  onRestorePasswordRedirect: PropTypes.func,
};

export default observer(Header);
